import { useState } from "react"
import { trpc } from "~/lib/trpc"
import { useAuth } from "~/lib/auth-context"
import { Button } from "@aix/ui"
import { useParams, useNavigate } from "@tanstack/react-router"
import { ExternalLink, Loader2, ArrowLeft, Pencil, Link2, AlertTriangle } from "lucide-react"
import toast from "react-hot-toast"
import { useOpenTabs, normalizeUrl } from "./hooks/useOpenTabs"
import { PackFormModal } from "./PackFormModal"

export function LinkPackDetailScreen() {
  const { slug } = useParams({ strict: false }) as { slug?: string }
  const { user } = useAuth()
  const navigate = useNavigate()
  const { openAll, blocked, clearBlocked } = useOpenTabs()
  const [showEdit, setShowEdit] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)

  const {
    data: pack,
    isLoading,
    error,
  } = trpc.linkPacks.getBySlug.useQuery({ slug: slug ?? "" }, { enabled: Boolean(slug) })

  const handleOpenAll = () => {
    if (!pack) return
    setHasOpened(true)
    openAll(pack.links.map((l) => l.url))
  }

  const copyShareLink = () => {
    void navigator.clipboard.writeText(window.location.href)
    toast.success("Link copied — paste it in Slack")
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !pack) {
    return (
      <div className="max-w-xl mx-auto text-center py-20">
        <h1 className="text-2xl font-bold mb-2">Pack not found</h1>
        <p className="text-muted-foreground mb-6">This tab pack doesn’t exist or was deleted.</p>
        <Button variant="outline" onClick={() => navigate({ to: "/linkpacks" })}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to all packs
        </Button>
      </div>
    )
  }

  const someBlocked = hasOpened && blocked.length > 0

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate({ to: "/linkpacks" })}
        className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        All packs
      </button>

      <div className="flex items-start gap-4 mb-6">
        <div className="text-5xl leading-none flex-shrink-0">{pack.emoji || "🗂️"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-3xl font-bold text-foreground">{pack.name}</h1>
            {user && (
              <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
          </div>
          {pack.description && <p className="text-muted-foreground mt-1">{pack.description}</p>}
        </div>
      </div>

      {/* Primary action */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Button onClick={handleOpenAll} size="lg" className="flex-1 text-base h-12">
          <ExternalLink className="w-5 h-5 mr-2" />
          Open all {pack.links.length} tabs
        </Button>
        <Button variant="outline" size="lg" className="h-12" onClick={copyShareLink}>
          <Link2 className="w-4 h-4 mr-2" />
          Copy share link
        </Button>
      </div>

      {/* Popup-blocked recovery banner */}
      {someBlocked && (
        <div className="flex gap-3 p-4 mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground">
              Your browser blocked {blocked.length} of {pack.links.length} tabs.
            </p>
            <p className="text-muted-foreground mt-1">
              Click the blocked pop-ups icon in the address bar, choose{" "}
              <span className="font-medium text-foreground">“Always allow pop-ups from this site”</span>, then press the
              button again. Or open the links one at a time below.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                clearBlocked()
                handleOpenAll()
              }}
            >
              Try again
            </Button>
          </div>
        </div>
      )}

      {/* Link list — always-available fallback */}
      <div className="border border-border rounded-lg divide-y divide-border">
        {pack.links.map((link, i) => {
          const href = normalizeUrl(link.url)
          const wasBlocked = someBlocked && blocked.includes(href)
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 hover:bg-secondary/50 transition-colors"
            >
              <span className="text-xs text-muted-foreground w-5 text-right flex-shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{link.label || href}</p>
                {link.label && <p className="text-xs text-muted-foreground truncate">{href}</p>}
              </div>
              {wasBlocked && (
                <span className="text-xs text-amber-500 font-medium flex-shrink-0">blocked — click to open</span>
              )}
              <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </a>
          )
        })}
      </div>

      {showEdit && <PackFormModal pack={pack} onClose={() => setShowEdit(false)} onSaved={() => setShowEdit(false)} />}
    </div>
  )
}
